import React, { useRef, memo } from 'react';
import { useItemVisibility } from './hooks/useItemVisibility';
import { getItemKey } from './utils/visibilityCalculations';
import type {
  CroppedRowProps,
  ItemElement,
  Item,
  Items,
  RenderItem,
  RenderMoreItems,
  RenderItemProps,
  RenderMoreItemsProps,
  ItemRefCallback,
} from './types';

// Re-export types for consumers
export type {
  ItemElement,
  Item,
  Items,
  RenderItem,
  RenderMoreItems,
  RenderItemProps,
  RenderMoreItemsProps,
  ItemRefCallback,
};

function CroppedRow(props: CroppedRowProps) {
  const {
    className,
    items,
    renderItem,
    renderMoreItems,
    renderAfter,
    offsetWidth,
    withoutCrop,
    maxWidth,
  } = props;

  const containerRef = useRef<HTMLDivElement>(null);

  const { visibleIds, itemRefs, hasMore } = useItemVisibility({
    items,
    offsetWidth,
    withoutCrop,
    maxWidth,
    containerRef,
  });

  return (
    <div className={className} ref={containerRef}>
      {items.map((item, index) => {
        const itemKey = getItemKey(item);
        const isPrevVisible =
          items[index - 1] && visibleIds.includes(getItemKey(items[index - 1]));
        const isVisible = visibleIds.includes(itemKey);
        const refFunc: ItemRefCallback = (node) => {
          if (node) {
            // Cast to ItemElement - assumes the element has data-id attribute
            itemRefs.current.set(itemKey, node as ItemElement);
          } else {
            itemRefs.current.delete(itemKey);
          }
        };
        const itemComponent = renderItem({
          item,
          ref: refFunc,
          isVisible,
          key: itemKey,
        });
        if (hasMore && isPrevVisible && !isVisible) {
          return (
            <React.Fragment key={itemKey}>
              {renderMoreItems({
                item,
                items: items.filter(
                  (moreItem) => !visibleIds.includes(getItemKey(moreItem)),
                ),
              })}
              {itemComponent}
            </React.Fragment>
          );
        }
        return itemComponent;
      })}
      {renderAfter}
    </div>
  );
}

export default memo(CroppedRow);
